import {
  effectiveParentId,
  isChildFolded,
  isProjectSelectable,
  type LiveProject,
  type ProjectsById,
} from '@wiztime/core';

/**
 * Project hierarchy as the pickers and the quick-mark card show it (docs/design/project-hierarchy-ui.md):
 * a selectable top-level project with its selectable child projects, split into the children that
 * are shown right away and the ones folded under "earlier" because they were not used for
 * `childFoldDays` days.
 */
export interface ProjectGroup {
  readonly project: LiveProject;
  /** Children shown when the group is expanded. */
  readonly fresh: readonly LiveProject[];
  /** Children folded under "earlier"; revealed by the "earlier" toggle. */
  readonly folded: readonly LiveProject[];
}

export interface FoldContext {
  readonly byId: ProjectsById;
  /** Latest personal date with a confirmed block, per project id (`lastUsedDateByProject`). */
  readonly lastUsed: ReadonlyMap<string, string>;
  readonly today: string;
  readonly foldDays: number;
}

/**
 * Groups `ordered` (projects in the display order wanted, children included) into top-level
 * projects with their children. Both levels keep the order of `ordered`; projects that are not
 * selectable (deactivated, or under a deactivated parent) are left out.
 */
export function buildProjectGroups(ordered: readonly LiveProject[], fold: FoldContext): ProjectGroup[] {
  const selectable = ordered.filter((project) => isProjectSelectable(project, fold.byId));
  const childrenByParent = new Map<string, LiveProject[]>();
  const tops: LiveProject[] = [];
  for (const project of selectable) {
    const parentId = effectiveParentId(project, fold.byId);
    if (parentId === null) tops.push(project);
    else childrenByParent.set(parentId, [...(childrenByParent.get(parentId) ?? []), project]);
  }
  return tops.map((project) => {
    const fresh: LiveProject[] = [];
    const folded: LiveProject[] = [];
    for (const child of childrenByParent.get(project.id) ?? []) {
      const isFolded = isChildFolded(child, fold.byId, fold.lastUsed.get(child.id) ?? null, fold.today, fold.foldDays);
      (isFolded ? folded : fresh).push(child);
    }
    return { project, fresh, folded };
  });
}

/** Groups without children: the flat list of a caller that has no hierarchy to show. */
export function flatGroups(projects: readonly LiveProject[]): ProjectGroup[] {
  return projects.map((project) => ({ project, fresh: [], folded: [] }));
}

/** The top-level projects of `groups`, in order: what the digit keys 1-9 and the tile ordinals count. */
export function topLevelOf(groups: readonly ProjectGroup[]): LiveProject[] {
  return groups.map((group) => group.project);
}

/** Number of children a group can show (shown plus folded). */
export const childCountOf = (group: ProjectGroup): number => group.fresh.length + group.folded.length;

/** One row of the flattened, expanded list. */
export type GroupRow =
  | { readonly kind: 'project'; readonly project: LiveProject; readonly level: 0 | 1; readonly group: ProjectGroup }
  | { readonly kind: 'earlier'; readonly group: ProjectGroup; readonly open: boolean };

export interface ExpandState {
  /** Parent ids whose children are shown. */
  readonly expanded: ReadonlySet<string>;
  /** Parent ids whose folded children are shown. */
  readonly earlierOpen: ReadonlySet<string>;
}

/**
 * The rows to render: every top-level project, and under an expanded one its fresh children, an
 * "earlier" toggle row when children are folded, and (when that is open) the folded children.
 * `forceOpen` ids (the group of the block's current project) count as expanded with "earlier" open.
 */
export function groupRows(groups: readonly ProjectGroup[], state: ExpandState, forceOpen?: ReadonlySet<string>): GroupRow[] {
  return groups.flatMap((group): GroupRow[] => {
    const rows: GroupRow[] = [{ kind: 'project', project: group.project, level: 0, group }];
    const forced = forceOpen?.has(group.project.id) === true;
    if (!forced && !state.expanded.has(group.project.id)) return rows;
    for (const child of group.fresh) rows.push({ kind: 'project', project: child, level: 1, group });
    if (group.folded.length > 0) {
      const open = forced || state.earlierOpen.has(group.project.id);
      rows.push({ kind: 'earlier', group, open });
      if (open) for (const child of group.folded) rows.push({ kind: 'project', project: child, level: 1, group });
    }
    return rows;
  });
}

/** Returns a new set with `id` added when absent and removed when present. */
export function toggled(set: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Selectable top-level projects in settings order: the quick-mark tiles and what the digit keys
 * 1-9 count. A child project never takes a digit; an expanded group does not change the numbering.
 */
export function topLevelSelectable(all: readonly LiveProject[], byId: ProjectsById): LiveProject[] {
  return all.filter((project) => isProjectSelectable(project, byId) && effectiveParentId(project, byId) === null);
}
